import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * Screen-reader announcer (`aria-live="polite"` by default).
 *
 * Use `useAnnouncer()` from anywhere to push a message to AT.
 *
 *   const announce = useAnnouncer();
 *   announce("AI draft ready");
 *
 * Two regions are mounted: one polite, one assertive. Messages alternate
 * between two slots to retrigger announcement when the same text repeats.
 */
type Politeness = "polite" | "assertive";
const AnnouncerContext = createContext<((msg: string, p?: Politeness) => void) | null>(null);

export function AnnouncerProvider({ children }: { children: React.ReactNode }) {
  const [politeA, setPoliteA] = useState("");
  const [politeB, setPoliteB] = useState("");
  const [assertA, setAssertA] = useState("");
  const [assertB, setAssertB] = useState("");
  const toggle = useRef(false);

  const announce = useCallback((msg: string, p: Politeness = "polite") => {
    if (!msg) return;
    toggle.current = !toggle.current;
    if (p === "assertive") {
      toggle.current ? setAssertA(msg) : setAssertB(msg);
    } else {
      toggle.current ? setPoliteA(msg) : setPoliteB(msg);
    }
  }, []);

  return (
    <AnnouncerContext.Provider value={announce}>
      {children}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        <span>{politeA}</span><span>{politeB}</span>
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        <span>{assertA}</span><span>{assertB}</span>
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer() {
  const ctx = useContext(AnnouncerContext);
  // Permissive fallback so components don't crash if provider missing.
  return ctx ?? ((_msg: string, _p?: Politeness) => { /* noop */ });
}

/** Announce on mount; useful for page-title changes. */
export function useAnnounceOnMount(msg: string, politeness: Politeness = "polite") {
  const announce = useAnnouncer();
  useEffect(() => { announce(msg, politeness); }, [msg, politeness, announce]);
}
